import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 400;

type UseSimulatorToolbarSearchArgs = {
    scenarioId?: number;
    searchInputId?: string;
};

export function useSimulatorToolbarSearch({ scenarioId }: UseSimulatorToolbarSearchArgs = {}) {
    const [searchInput, setSearchInput] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');

    const debouncedSetKeyword = useMemo(
        () =>
            debounce((value: string) => {
                setSearchKeyword(value.trim());
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => debouncedSetKeyword.cancel(), [debouncedSetKeyword]);

    useEffect(() => {
        setSearchInput('');
        setSearchKeyword('');
        debouncedSetKeyword.cancel();
    }, [debouncedSetKeyword, scenarioId]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            if (value === '') {
                debouncedSetKeyword.cancel();
                setSearchKeyword('');
                return;
            }
            debouncedSetKeyword(value);
        },
        [debouncedSetKeyword],
    );

    const handleSearchSubmit = useCallback(
        (override?: string) => {
            const nextValue = override !== undefined ? override : searchInput;
            debouncedSetKeyword.cancel();
            setSearchInput(nextValue);
            setSearchKeyword(nextValue.trim());
        },
        [debouncedSetKeyword, searchInput],
    );

    return {
        searchInput,
        searchKeyword,
        handleSearchChange,
        handleSearchSubmit,
    };
}
